import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { API, SITE, errMsg } from "@/lib/api";
import { Bubble } from "@/components/admin/ChatsTab";

const KEY = "inla_chat";
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } };

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white" aria-hidden="true"><path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5 2.5 1 3 .8 3.6.7.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2z" /></svg>
);

function StartForm({ onStarted }) {
  const [f, setF] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/chat/start`, { ...f, email: f.email || null });
      onStarted({ id: r.data.chat_id, token: r.data.token });
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  const cls = "w-full px-3 h-10 border border-slate-200 rounded-sm text-sm outline-none focus:border-[#FF6B00]";
  return (
    <form onSubmit={submit} className="p-4 space-y-3" data-testid="chat-start-form">
      <p className="text-sm text-slate-600">Hi! Ask us about a shipment, a quote or a delivery. We reply right here.</p>
      <input required maxLength={80} placeholder="Your name" className={cls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="chat-name-input" />
      <input type="email" placeholder="Email (optional, for follow-up)" className={cls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="chat-email-input" />
      <textarea required maxLength={2000} rows={3} placeholder="How can we help?" className={`${cls} h-auto py-2`} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="chat-first-message-input" />
      <button type="submit" disabled={busy} className="w-full h-10 bg-[#FF6B00] hover:bg-[#E05D00] text-white font-semibold text-sm rounded-sm flex items-center justify-center gap-2" data-testid="chat-start-btn">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Start chat
      </button>
    </form>
  );
}

function Conversation({ session, open, onUnread, onLost }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef(null);
  const headers = { "X-Chat-Token": session.token };
  const fetchMsgs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chat/${session.id}`, { headers });
      setMsgs(r.data.messages);
      onUnread(r.data.chat.unread_visitor || 0);
      if (open && r.data.chat.unread_visitor) await axios.post(`${API}/chat/${session.id}/read`, {}, { headers });
    } catch (e) { if (e?.response?.status === 404) onLost(); }
  }, [session.id, open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchMsgs();
    const t = setInterval(fetchMsgs, open ? 3000 : 12000);
    return () => clearInterval(t);
  }, [fetchMsgs, open]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs.length, open]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await axios.post(`${API}/chat/${session.id}/messages`, { text }, { headers }); setText(""); fetchMsgs(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  if (!open) return null;
  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.sender === "visitor"} />)}
        {msgs.length > 0 && msgs.every((m) => m.sender === "visitor") && <p className="text-xs text-center text-slate-400">Thanks! Our team will reply here shortly.</p>}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-slate-200 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Type a message..." className="flex-1 h-10 px-3 border border-slate-200 rounded-sm text-sm outline-none focus:border-[#FF6B00]" data-testid="chat-message-input" />
        <button type="submit" className="h-10 px-4 bg-[#FF6B00] hover:bg-[#E05D00] text-white rounded-sm" data-testid="chat-send-btn"><Send className="h-4 w-4" /></button>
      </form>
    </>
  );
}

export function ChatWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(load);
  const [unread, setUnread] = useState(0);
  const start = (s) => { localStorage.setItem(KEY, JSON.stringify(s)); setSession(s); };
  const lost = () => { localStorage.removeItem(KEY); setSession(null); };
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const id = p.get("chat"), token = p.get("key");
    if (id && token) {
      start({ id, token });
      setOpen(true);
      p.delete("chat"); p.delete("key");
      const qs = p.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);
  if (pathname.startsWith("/admin")) return null;
  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3 no-print">
      {open && (
        <div className="w-[calc(100vw-2.5rem)] max-w-sm h-[480px] bg-white border border-slate-200 rounded-md shadow-2xl flex flex-col overflow-hidden rise" data-testid="chat-panel">
          <div className="bg-[#0A192F] text-white px-4 py-3 flex items-center justify-between">
            <div><p className="font-display font-semibold">Live chat</p><p className="text-xs text-slate-400">Inla Shipping Hub support</p></div>
            <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="text-xs font-semibold px-2.5 py-1 rounded-sm bg-[#25D366] hover:bg-[#1ebe5b]" data-testid="chat-panel-whatsapp-link">WhatsApp</a>
          </div>
          {session ? <Conversation session={session} open={open} onUnread={setUnread} onLost={lost} /> : <StartForm onStarted={start} />}
        </div>
      )}
      {!open && session && <Conversation session={session} open={false} onUnread={setUnread} onLost={lost} />}
      <div className="flex items-center gap-3">
        <a href={SITE.whatsapp} target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp" className="h-14 w-14 rounded-full bg-[#25D366] hover:scale-105 transition-transform shadow-lg flex items-center justify-center" data-testid="whatsapp-float-btn"><WhatsAppIcon /></a>
        <button onClick={() => setOpen(!open)} aria-label="Open live chat" className="relative h-14 w-14 rounded-full bg-[#FF6B00] hover:scale-105 transition-transform shadow-lg flex items-center justify-center" data-testid="chat-toggle-btn">
          {open ? <X className="h-6 w-6 text-white" /> : <MessageCircle className="h-6 w-6 text-white" />}
          {!open && unread > 0 && <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-[#0A192F] text-white text-xs flex items-center justify-center" data-testid="chat-unread-dot">{unread}</span>}
        </button>
      </div>
    </div>
  );
}
